import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Alert from 'shared/components/ui/Alert';

// Bidirectional / account-reuse (GUNDI-5595): action configurations are stored on
// the INTEGRATION record, not on the Route, so every Route using this account
// inherits changes made here. Field mappings are NOT affected — those live on the
// Route and stay per-flow.
type SharedAccountNoticeProps = {
  // name is absent when the user can't resolve the Route (e.g. it lives in
  // a workspace they can't see) — those render as a count, never as ids/links.
  usedBy: { id: string; name?: string }[];
};

// Longer lists show this many Routes and a "Show more…" link for the rest.
const COLLAPSED_COUNT = 3;

const SharedAccountNotice = ({ usedBy }: SharedAccountNoticeProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  if (usedBy.length === 0) return null;
  const named = usedBy.filter((c) => c.name);
  const hiddenCount = usedBy.length - named.length;
  const collapsed = !expanded && named.length > COLLAPSED_COUNT;
  const shown = collapsed ? named.slice(0, COLLAPSED_COUNT) : named;
  return (
    <Alert status="warning" className="mb-5">
      <div className="text-body-sm text-ui-text-primary-text">
        <span className="font-bold">{t('Shared account.')}</span>{' '}
        {t('Changes to these settings can affect other Routes.')}
      </div>
      {(named.length > 0 || hiddenCount > 0) && (
        <div className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-1">
          {shown.map((c, i) => (
            <span key={c.id} className="text-body-sm text-ui-text-primary-text">
              <Link
                to={`/routes/${c.id}`}
                // Same look as the source links in the Sources tables.
                className="text-ui-cta-primary hover:underline"
              >
                {c.name}
              </Link>
              {(i < shown.length - 1 || hiddenCount > 0 || collapsed) && ','}
            </span>
          ))}
          {collapsed && (
            <>
              <span className="text-body-sm text-ui-text-primary-text">…</span>
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="cursor-pointer text-body-sm text-ui-cta-primary hover:underline"
              >
                {t('Show more…')}
              </button>
            </>
          )}
          {!collapsed && hiddenCount > 0 && (
            <span className="text-body-sm text-ui-text-primary-text">
              {hiddenCount === 1
                ? t("{{count}} Route in a workspace you can't view", {
                    count: hiddenCount,
                  })
                : t("{{count}} Routes in workspaces you can't view", {
                    count: hiddenCount,
                  })}
            </span>
          )}
        </div>
      )}
    </Alert>
  );
};

export default SharedAccountNotice;
